/**
 * Header Component
 *
 * Renders the main navigation bar for the Andoria AI website.
 * Features:
 * - Responsive navigation with desktop and mobile layouts
 * - Sticky header with scroll-based shadow effect
 * - Logo, navigation links, and call-to-action button
 * - Mobile drawer menu with overlay and smooth transitions
 *
 * @returns {JSX.Element} The site header with navigation and CTA
 */
'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/Button';

/**
 * Main Header functional component.
 * Uses React hooks for scroll and menu state management.
 */
export default function Header() {
  // State for mobile menu open/close
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // State for header shadow on scroll
  const [isScrolled, setIsScrolled] = useState(false);

  /**
   * Navigation links for the main menu.
   * Each link has a path and a label.
   */
  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/about', label: 'About Us' },
    { href: '/services', label: 'Services' },
    { href: '/events', label: 'Events' },
    { href: '/blog', label: 'Blog' },
    { href: '/#contact', label: 'Contact Us' },
  ];

  /**
   * Adds/removes shadow on the header when the user scrolls down.
   * Uses a scroll event listener to update state.
   */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Closes the mobile menu drawer.
   */
  const closeMenu = () => setIsMenuOpen(false);

  /**
   * Handles click on the "Book Consultation" CTA.
   * Smoothly scrolls to the contact section and closes the menu.
   */
  const handleBookClick = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    closeMenu();
  };

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 w-full bg-white py-4 px-4 sm:py-5 sm:px-6 lg:px-24 transition-all duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Logo section: Clickable, returns to home */}
        <Link href="/" className="flex items-center" onClick={closeMenu}>
          <Image
            src="/images/logo-andoria-black.png"
            alt="Andoria AI Logo"
            width={90}
            height={14.97}
            className="h-auto w-[80px] sm:w-[90px]"
            priority
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6 lg:space-x-10">
          {navLinks.map(({ href, label }) => (
            <Link
              key={label}
              href={href}
              className="relative text-gray-900 font-normal text-sm sm:text-base leading-6 sm:leading-8 hover:text-androia-blue transition-all duration-300 group"
            >
              {label}
              {/* Animated underline on hover */}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-androia-blue transition-all duration-300 group-hover:w-full" />
            </Link>
          ))}
        </nav>

        {/* Desktop CTA: Book Consultation button */}
        <Button
          className="hidden md:inline-flex bg-androia-gray-900 border border-androia-gray-500 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-gray-800 hover:scale-105 hover:shadow-lg transition-all duration-300"
          onClick={handleBookClick}
        >
          Book Consultation
        </Button>

        {/* Mobile Menu Button: Hamburger/close icon */}
        <button
          className="md:hidden p-1.5 focus:outline-none"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 sm:w-6 sm:h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Overlay: Darkens background when menu is open */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 md:hidden ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={closeMenu}
        aria-hidden={!isMenuOpen}
      />

      {/* Mobile Drawer: Slide-in menu from right */}
      <div
        className={`fixed top-0 right-0 h-full w-4/5 max-w-sm bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isMenuOpen}
      >
        {/* Close Button for mobile drawer */}
        <div className="flex flex-col h-full p-4 sm:p-6">
          <div className="flex justify-end mb-6 sm:mb-8">
            <button
              className="p-1.5 sm:p-2 focus:outline-none"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation Links for mobile */}
          <nav className="flex flex-col space-y-4 sm:space-y-6 flex-grow">
            {navLinks.map(({ href, label }) => (
              <Link
                key={label}
                href={href}
                className="text-gray-900 font-medium text-base sm:text-lg hover:text-androia-blue transition-colors duration-300"
                onClick={closeMenu}
              >
                {label}
              </Link>
            ))}
          </nav>

          {/* CTA Button at bottom of mobile drawer */}
          <div className="mt-auto pt-4 sm:pt-6">
            <Button
              className="w-full bg-androia-gray-900 border border-androia-gray-500 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 sm:py-4 rounded-lg hover:bg-gray-800 transition-all duration-300"
              onClick={handleBookClick}
            >
              Book Consultation
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
